import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, tap, catchError, delay } from 'rxjs/operators';
import {
  E621ApiResponse,
  E621Post,
  VideoSearchResult,
  SearchParams,
  ApiSettings,
} from '../models/e621.models';
import { SeenPersistenceService } from './seen-persistence.service';

@Injectable({
  providedIn: 'root',
})
export class E621Service {
  private readonly API_BASE_URL = 'https://e621.net';
  private readonly USER_AGENT = 'DrawfiendShow/1.0 (Web Media Player)';

  private http = inject(HttpClient);
  private seenPersistenceService = inject(SeenPersistenceService);
  private apiSettings$ = new BehaviorSubject<ApiSettings>({});
  private cache = new Map<string, VideoSearchResult>();

  constructor() {
    this.loadApiSettings();
  }

  /**
   * Load API settings from localStorage
   */
  private loadApiSettings(): void {
    try {
      const saved = localStorage.getItem('e621-api-settings');
      if (saved) {
        const settings = JSON.parse(saved);
        this.apiSettings$.next(settings);
        console.log('API settings loaded from localStorage');
      } else {
        // Set default blacklist if no settings exist
        const defaultSettings: ApiSettings = {
          blacklist: ['young', 'gore', 'scat', 'watersports', 'vore']
        };
        this.saveApiSettings(defaultSettings);
        console.log('Default blacklist applied');
      }
    } catch (error) {
      console.error('Error loading API settings:', error);
    }
  }

  /**
   * Save API settings to localStorage
   */
  saveApiSettings(settings: ApiSettings): void {
    try {
      localStorage.setItem('e621-api-settings', JSON.stringify(settings));
      this.apiSettings$.next(settings);
      console.log('API settings saved');
    } catch (error) {
      console.error('Error saving API settings:', error);
    }
  }

  /**
   * Get current API settings
   */
  getApiSettings(): Observable<ApiSettings> {
    return this.apiSettings$.asObservable();
  }

  /**
   * Get current API settings synchronously
   */
  getCurrentApiSettings(): ApiSettings {
    return this.apiSettings$.value;
  }  /**
   * Search for video posts on e621
   *
   * @param searchParams Search parameters including tags, limit, and page
   * @returns Observable of VideoSearchResult
   *
   * @note Searches with 'order:random' or 'order:rand' are NOT cached to ensure
   *       each request returns different random results
   */
  searchVideos(searchParams: SearchParams): Observable<VideoSearchResult> {// Check if this is a random search - if so, skip caching
    // Handle various random order formats: order:random, order:rand, ~order:random, etc.
    const isRandomSearch = /order:rand(om)?/i.test(searchParams.tags);

    const cacheKey = this.getCacheKey(searchParams);

    // Check cache first (only if not a random search)
    if (!isRandomSearch && this.cache.has(cacheKey)) {
      console.log('Returning cached results for:', searchParams.tags);
      // Use delay(0) to ensure cached results are returned asynchronously
      // This maintains consistent behavior with network requests and prevents
      // the loading spinner from getting stuck when doing duplicate searches
      console.log('Cache hit for:', cacheKey);
      return of(this.cache.get(cacheKey)!).pipe(delay(0));
    }

    if (isRandomSearch) {
      console.log('Random search detected - skipping cache for:', searchParams.tags);
    }

    const videoQuery = `${searchParams.tags}`;

    let params = new HttpParams()
      .set('tags', videoQuery)
      .set('limit', (searchParams.limit || 200).toString());

    if (searchParams.page) {
      params = params.set('page', searchParams.page.toString());
    }

    // Add authentication parameters if available
    const settings = this.apiSettings$.value;
    if (settings.username && settings.apiKey) {
      params = params.set('login', settings.username);
      params = params.set('api_key', settings.apiKey);
      console.log(
        'Using authenticated API request for user:',
        settings.username
      );
    } else {
      console.log('Using anonymous API request');
    }
    const headers = this.buildHeaders();
    const finalUrl = `${this.API_BASE_URL}/posts.json?${params.toString()}`;
    console.log(
      'Searching e621 API with query (with forced video filter):',
      videoQuery
    );
    console.log('Encoded parameters:', params.toString());
    console.log('Full API URL:', finalUrl);

    return this.http
      .get<E621ApiResponse>(`${this.API_BASE_URL}/posts.json`, {
        params,
        headers,
      })
      .pipe(
        map((response) => this.filterAndTransformVideos(response.posts)),        tap((result) => {
          console.log(
            `Found ${result.videos.length} video posts (${result.authRequiredCount} filtered due to auth requirements)`
          );
          // Cache the results (only if not a random search)
          if (!isRandomSearch) {
            this.cache.set(cacheKey, result);
            console.log('Results cached for key:', cacheKey);
          } else {
            console.log('Skipped caching for random search');
          }
        }),
        catchError((error) => {
          console.error('Error searching e621 API:', error);
          console.error('Failed URL:', finalUrl);
          console.error('Response status:', error.status);
          console.error('Response body:', error.error);

          if (error.status === 403) {
            console.error(
              'API access denied. Check your API key and username.'
            );
          } else if (error.status === 429) {
            console.error(
              'Rate limit exceeded. Please wait before making more requests.'
            );
          } else if (error.status === 400) {
            console.error(
              'Bad request. The search query may be malformed:',
              videoQuery
            );
          } else if (error.status === 0) {
            console.error(
              'Network error. Check CORS settings and internet connection.'
            );
          }
          return of({
            videos: [],
            authRequiredCount: 0,
            blacklistedCount: 0,
            totalFound: 0,
          });
        })
      );
  }
  /**
   * Build HTTP headers for API requests
   */
  private buildHeaders(): HttpHeaders {
    return new HttpHeaders({
      // 'User-Agent': this.USER_AGENT,
    });
  }
  /**
   * Filter posts to videos and images and transform to simplified format
   */
  private filterAndTransformVideos(posts: E621Post[]): VideoSearchResult {
    const totalFound = posts.length;
    let authRequiredCount = 0;
    let blacklistedCount = 0;

    // Get current blacklist
    const currentSettings = this.getCurrentApiSettings();
    const blacklist = currentSettings.blacklist || [];

    const validPosts = posts.filter((post) => {
      if (!post.file) {
        return false;
      }

      const fileExt = post.file.ext.toLowerCase();

      // Check if this is a video or image file
      const isVideo = ['webm', 'mp4'].includes(fileExt);
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExt);

      if (!isVideo && !isImage) {
        console.log(
          `Skipping unsupported post ${post.id} with extension: ${post.file?.ext}`
        );
        return false;
      }

      // Check if authentication is required (null URL)
      if (!post.file.url) {
        console.log(`Skipping post ${post.id} - authentication required`);
        authRequiredCount++;
        return false;
      }

      // Check blacklist
      if (blacklist.length > 0) {
        const allTags = [
          ...post.tags.general,
          ...post.tags.species,
          ...post.tags.character,
          ...post.tags.copyright,
          ...post.tags.artist,
          ...post.tags.meta,
          ...post.tags.lore,
        ];

        const isBlacklisted = blacklist.some(blacklistedTag =>
          allTags.some(tag => tag.toLowerCase().includes(blacklistedTag.toLowerCase()))
        );

        if (isBlacklisted) {
          console.log(`Skipping post ${post.id} - matches blacklist`);
          blacklistedCount++;
          return false;
        }
      }

      // Check seen blacklist
      const seenSettings = this.seenPersistenceService.getSeenSettings();
      if (seenSettings.enableSeenBlacklist && this.seenPersistenceService.isVideoSeen(post.id)) {
        console.log(`Skipping post ${post.id} - already seen`);
        blacklistedCount++; // Count seen media as part of blacklisted for stats
        return false;
      }

      return true;
    });

    const videos = validPosts.map((post) => {
      const fileExt = post.file.ext.toLowerCase();
      const isVideo = ['webm', 'mp4'].includes(fileExt);
      const isAnimated = fileExt === 'gif' && post.duration != null && post.duration > 0;

      return {
        id: post.id,
        url: post.file.url,
        previewUrl: post.preview.url,
        sampleUrl: post.sample.has ? post.sample.url : '',
        duration: post.duration,
        width: post.file.width,
        height: post.file.height,
        tags: [
          ...post.tags.general,
          ...post.tags.species,
          ...post.tags.character,
          ...post.tags.copyright,
          ...post.tags.artist,
        ],
        rating: post.rating,
        // Enhanced information
        tagsByCategory: {
          general: post.tags.general,
          species: post.tags.species,
          character: post.tags.character,
          copyright: post.tags.copyright,
          artist: post.tags.artist,
          meta: post.tags.meta,
          lore: post.tags.lore,
        },
        score: post.score,
        favCount: post.fav_count,
        createdAt: post.created_at,
        description: post.description,
        sources: post.sources,
        fileSize: post.file.size,
        fileExt: post.file.ext,
        commentCount: post.comment_count,
        // New fields for image support
        contentType: isVideo ? 'video' : 'image' as 'video' | 'image',
        isAnimated: isAnimated ? true : undefined,
      };
    });

    return {
      videos,
      authRequiredCount,
      blacklistedCount,
      totalFound,
    };
  }  /**
   * Generate cache key for search parameters
   * Note: Random searches are not cached, but we still generate keys for logging
   */
  private getCacheKey(searchParams: SearchParams): string {
    const currentSettings = this.getCurrentApiSettings();

    // Include API settings in cache key to ensure different credentials/blacklists get separate cache entries
    const settingsKey = JSON.stringify({
      username: currentSettings.username || '',
      apiKey: currentSettings.apiKey || '',
      blacklist: currentSettings.blacklist || []
    });    // Include a random search indicator in the key for clarity
    const isRandom = /order:rand(om)?/i.test(searchParams.tags);
    const randomIndicator = isRandom ? '_RANDOM' : '';

    return `${searchParams.tags}_${searchParams.limit || 200}_${
      searchParams.page || 1
    }_${btoa(settingsKey)}${randomIndicator}`; // Use base64 to make the key URL-safe
  }
  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('Cache cleared');
  }

  /**
   * Clear only non-random cached entries (useful when you want to refresh normal searches but keep random separate)
   */
  clearNonRandomCache(): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => !key.includes('_RANDOM'));
    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`Cleared ${keysToDelete.length} non-random cache entries`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Test API credentials by calling /users/me.json
   * Returns Observable<boolean>: true if valid, false if invalid
   */
  testApiCredentials(username: string, apiKey: string): Observable<boolean> {
    const params = new HttpParams()
      .set('login', username)
      .set('api_key', apiKey);
    const headers = new HttpHeaders({});
    return this.http.get<{ id?: number }>(`${this.API_BASE_URL}/users/me.json`, { params, headers }).pipe(
      map((response) => !!response && !!response.id),
      catchError(() => {
        // 401/403 means invalid credentials
        return of(false);
      })
    );
  }
}
